import { getOwnCompanyData } from 'API/company';
import { Company } from 'types/company.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import useErrorStore from 'stores/error-store';
import useCountries from './use-countries';
import { useUserData } from './use-user-data';

export interface CompanyInfo {
  name: string;
  street: string;
  zip: string;
  city: string;
  country: OptionType | null;
}

const defaultForm: CompanyInfo = {
  name: '',
  street: '',
  zip: '',
  city: '',
  country: null,
};

const hasRequiredCompanyInfo = (company?: Company) => {
  return !!(
    company?.name?.trim() &&
    company.addressLine1?.trim() &&
    company.postalCode?.trim() &&
    company.city?.trim() &&
    company.country?.trim()
  );
};

const useOwnCompany = () => {
  useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const { countryOptions, fetchCountries } = useCountries();

  const [ownCompany, setOwnCompany] = useState<Company>();
  const [isCompanyMissing, setIsCompanyMissing] = useState(false);

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(defaultForm);

  const fetchOwnCompany = async (checkForEmptyFields = true) => {
    const res = await getOwnCompanyData();

    if (!res.error) {
      const resCompany = new Company(res.data);
      setOwnCompany(resCompany);

      setIsCompanyMissing(checkForEmptyFields && !hasRequiredCompanyInfo(resCompany));

      if (!_.isNil(resCompany.name)) {
        setCompanyInfo((prev) => ({ ...prev, name: resCompany.name }));
      }
      if (!_.isNil(resCompany.addressLine1)) {
        setCompanyInfo((prev) => ({ ...prev, street: resCompany.addressLine1 }));
      }
      if (!_.isNil(resCompany.postalCode)) {
        setCompanyInfo((prev) => ({ ...prev, zip: resCompany.postalCode }));
      }
      if (!_.isNil(resCompany.city)) {
        setCompanyInfo((prev) => ({ ...prev, city: resCompany.city }));
      }
    } else {
      handleOpenErrorDialog(res?.error, '/tenants/current', 'get');
    }
  };

  useEffect(() => {
    fetchOwnCompany();
  }, []);

  useEffect(() => {
    if (!_.isNil(ownCompany?.country)) {
      const selectedCountry = countryOptions.find((option) => option.value === ownCompany.country) || null;
      setCompanyInfo((prev) => ({ ...prev, country: selectedCountry }));
    }
  }, [ownCompany, countryOptions]);

  const handleCompanyInfoChange = (field: keyof CompanyInfo) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setCompanyInfo((prev) => ({ ...prev, [field]: event.target.value }));
    };
  };

  const handleCountryChange = (event: React.ChangeEvent<any>, newValue: OptionType) => {
    setCompanyInfo((prev) => ({ ...prev, country: newValue }));
  };

  const resetCompanyInfo = () => {
    setCompanyInfo(defaultForm);
  };

  return {
    ownCompany,
    isCompanyMissing,
    companyInfo,
    handleCompanyInfoChange,
    handleCountryChange,
    resetCompanyInfo,
    fetchOwnCompany,
    countryOptions,
    fetchCountries,
  };
};

export default useOwnCompany;
